import type { AdminMemberTracking } from "@ternary/contracts"
import { Alert, Button, Card, Checkbox } from "@ternary/ui"

const DATE = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" })

/** When members were last checked, in plain words: "just now", "3 min ago", "3 h ago" or a date. */
export function checkedLabel(checkedAt: string | null, now: Date): string {
  if (!checkedAt) return "Members not checked yet"
  const minutes = Math.floor((now.getTime() - Date.parse(checkedAt)) / 60_000)
  if (minutes < 1) return "Members checked just now"
  if (minutes < 60) return `Members checked ${minutes} min ago`
  if (minutes < 24 * 60) return `Members checked ${Math.floor(minutes / 60)} h ago`
  return `Members checked ${DATE.format(new Date(checkedAt))}`
}

/**
 * Member tracking on the Discord page: "Track who is in your server", when members were last
 * checked with Check now, why checks are slower without Discord's member list, and a re-add link
 * for a server the bot left. It only shows: the page saves through `onTrackingChange` and
 * `onCheckNow`.
 */
export function MemberTrackingCard({
  checking,
  guilds,
  now = new Date(),
  onCheckNow,
  onTrackingChange,
  pending,
  tracking,
  writable,
}: {
  /** Check now is on its way. */
  checking: boolean
  /** The business's servers, for their names and re-add links. */
  guilds: ReadonlyArray<{ id: string; name: string; reinstallUrl?: string | undefined }>
  now?: Date
  onCheckNow(): void
  onTrackingChange(tracking: boolean): void
  /** The toggle is saving. */
  pending: boolean
  tracking: AdminMemberTracking
  writable: boolean
}) {
  const nameOf = (id: string) => guilds.find((guild) => guild.id === id)?.name || "your server"
  const gone = tracking.guilds.filter(({ state }) => state === "bot_gone")
  const busy = tracking.guilds.some(({ state }) => state === "checking")
  return (
    <Card title="Member tracking">
      <div className="admin-stack admin-stack-tight">
        <Checkbox
          checked={tracking.tracking}
          disabled={!writable || pending}
          hint="Ternary checks your server every hour and before every export. Members who leave are marked Left and drop out of exports filtered to the server."
          label="Track who is in your server"
          onChange={(event) => onTrackingChange(event.currentTarget.checked)}
        />
        {writable ? null : <p className="admin-note">Only owners and admins can change this.</p>}
        {tracking.tracking ? (
          <div className="admin-row">
            <p className="admin-note" role="status">
              {busy ? "Checking members…" : checkedLabel(tracking.checkedAt, now)}
            </p>
            {writable ? (
              <Button loading={checking} onClick={onCheckNow} size="sm" variant="secondary">
                Check now
              </Button>
            ) : null}
          </div>
        ) : null}
        {tracking.tracking && tracking.mode === "probe" ? (
          <Alert tone="info">
            Discord doesn't give Ternary's bot your server's member list, so members are checked one
            at a time. A full check is slower, and exports show members as of the last full check.
          </Alert>
        ) : null}
        {tracking.tracking
          ? gone.map(({ id }) => {
              const href = guilds.find((guild) => guild.id === id)?.reinstallUrl
              return (
                <Alert
                  action={
                    writable && href ? (
                      <a className="tny-button tny-button-secondary admin-button-link" href={href}>
                        Re-add the bot
                      </a>
                    ) : undefined
                  }
                  key={id}
                  tone="warning"
                >
                  {`The bot isn't in ${nameOf(id)}, so its members can't be checked.`}
                </Alert>
              )
            })
          : null}
      </div>
    </Card>
  )
}
